import React, { useState, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Lazy-load the heavy Leaflet map so the signup form is interactive immediately.
// Root cause of the reported "signup page freezes" — the map's tile fetches
// (react-leaflet + leaflet + OpenStreetMap CDN) block the initial render on
// mobile. Suspense keeps the map optional and non-blocking.
const LocationPicker = React.lazy(() => import("@/components/LocationPicker"));

export default function Register() {
  const { t, lang } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [location, setLocation] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    // Trim inputs — mobile keyboards sometimes auto-add spaces
    const r = await register(form.email.trim(), form.password.trim(), form.name.trim(), location);
    setBusy(false);
    if (r.ok) {
      toast.success(t("welcome"));
      navigate("/");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 page-enter">
      <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">M16</div>
      <h1 className="font-display text-4xl mb-8">{t("createAccount")}</h1>

      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <input
          required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder={t("name")} className="input-luxe" data-testid="register-name-input"
        />
        <input
          type="email" required value={form.email} dir="ltr" autoComplete="email" autoCapitalize="none" spellCheck={false}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder={t("email")} className="input-luxe" data-testid="register-email-input"
        />
        <input
          type="password" required minLength={6} value={form.password} dir="ltr" autoComplete="new-password" autoCapitalize="none" spellCheck={false}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={t("password")} className="input-luxe" data-testid="register-password-input"
        />

        <div className="pt-3">
          <Suspense fallback={
            <div className="rounded-xl border border-[#27272A] p-6 flex items-center justify-center text-[#9CA3AF] text-sm gap-2" style={{ height: 280 }}>
              <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
              {lang === "ar" ? "جاري تحميل الخريطة..." : "Loading map..."}
            </div>
          }>
            <LocationPicker value={location} onChange={setLocation} />
          </Suspense>
          <p className="text-xs text-[#9CA3AF] mt-2">
            {lang === "ar"
              ? "اختياري — يساعدنا في توصيل طلباتك بدقة أكبر."
              : "Optional — helps us deliver your orders accurately."}
          </p>
        </div>

        <button type="submit" disabled={busy} className="btn-gold w-full py-3 rounded-full inline-flex items-center justify-center gap-2" data-testid="register-submit-btn">
          {busy && <Loader2 size={14} className="animate-spin" />}
          {busy ? (lang === "ar" ? "جاري الإنشاء..." : "Creating...") : t("createAccount")}
        </button>
      </form>

      <p className="text-sm text-[#9CA3AF] mt-6 text-center">
        {t("haveAccount")}{" "}
        <Link to="/login" data-testid="goto-login-link" className="text-[#D4AF37] hover:underline">
          {t("signIn")}
        </Link>
      </p>
    </div>
  );
}
